import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HttpTransportType } from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { NotificationDto } from '../../modules/association/models/notification.models';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SignalRNotificationService {
  private hubConnection: HubConnection;
  private notificationSubject = new BehaviorSubject<NotificationDto | null>(null);
  public onNotificationReceived$ = this.notificationSubject.asObservable();
  private isInitialized: boolean = false;
  private connectionPromise: Promise<void> | null = null;
  private hasLoggedConnectionError: boolean = false;

  constructor() {
    this.initializeConnection();
  }

  private initializeConnection(): void {
    // Remove the trailing /api/ from APIURL and add notificationHub
    const hubUrl = environment.APIURL.replace('/api/', '/notificationHub');

    this.hubConnection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        skipNegotiation: false,
        transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling,
        withCredentials: false  // Disable credentials to avoid CORS wildcard issue
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          // Progressive backoff: 0s, 2s, 10s, 30s, then 60s
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          if (retryContext.previousRetryCount === 3) return 30000;
          return 60000;
        }
      })
      .build();

    this.hubConnection.on('ReceiveNotification', (notification: NotificationDto) => {
      this.notificationSubject.next(notification);
    });

    this.hubConnection.onreconnecting(error => {
      console.log('SignalR reconnecting...');
    });

    this.hubConnection.onreconnected(connectionId => {
      console.log('SignalR reconnected');
      this.hasLoggedConnectionError = false;
    });

    this.hubConnection.onclose(error => {
      if (error) {
        // Only log once to avoid spam
        if (!this.hasLoggedConnectionError) {
          console.warn('SignalR unavailable - real-time notifications disabled');
          this.hasLoggedConnectionError = true;
        }
      }
    });
  }

  startConnection(): Promise<void> {
    // Return existing promise if connection is in progress
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    // Already connected
    if (this.hubConnection.state === 'Connected') {
      this.isInitialized = true;
      return Promise.resolve();
    }

    // Start connection and cache the promise
    this.connectionPromise = this.hubConnection.start()
      .then(() => {
        console.log('SignalR Notification Connected');
        this.isInitialized = true;
        this.hasLoggedConnectionError = false;
        this.connectionPromise = null; // Clear the promise after success
      })
      .catch(err => {
        // Only log once to avoid spam
        if (!this.hasLoggedConnectionError) {
          console.warn('SignalR connection failed - real-time notifications disabled');
          this.hasLoggedConnectionError = true;
        }
        this.connectionPromise = null; // Clear the promise to allow retry
        // Don't throw - allow app to continue without SignalR
        return Promise.resolve();
      });

    return this.connectionPromise;
  }

  stopConnection(): void {
    if (this.hubConnection && this.hubConnection.state === 'Connected') {
      this.hubConnection.stop().catch(err => {
        // Silently fail
      });
      this.isInitialized = false;
    }
  }

  joinUserGroup(userId: string): Promise<void> {
    if (this.hubConnection.state !== 'Connected') {
      return Promise.resolve();
    }
    return this.hubConnection.invoke('JoinUserGroup', userId)
      .catch(err => {
        // Silently fail - user just won't get real-time notifications
      });
  }

  leaveUserGroup(userId: string): Promise<void> {
    if (this.hubConnection.state === 'Connected') {
      return this.hubConnection.invoke('LeaveUserGroup', userId)
        .catch(err => {
          // Silently fail
        });
    }
    return Promise.resolve();
  }

  get isConnected(): boolean {
    return this.hubConnection.state === 'Connected';
  }
}
